import {
  Button,
  Flex,
  Loading,
  Table,
  type TableColumnsType,
  TableRowMenuCell,
} from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import {
  FALLBACK_TEXT,
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_DATE_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  dateFormat,
  datetimeFormat,
} from '@/utils';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useMinistries } from '../../hooks';
import type { MinistryModel } from '../../models/ministry';
import { useDrawerState } from '../DrawerStateProvider';
import { DeleteMinistryConfirmModal } from './DeleteMinistryConfirmModal';
import { MinistryFormDrawer } from './MinistryFormDrawer';
import { MinistrySortableTable } from './MinistrySortableTable';

export const MinistriesPage: FC = () => {
  const { t } = useTranslation();
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const { setDrawerState } = useDrawerState();
  const ministries = useMinistries();
  const editPermission = useMasterTablePermission(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const columns: TableColumnsType<z.infer<typeof MinistryModel>> = useMemo(
    () => [
      {
        title: t('code'),
        key: 'code',
        dataIndex: 'code',
        width: TABLE_CODE_WIDTH,
      },
      {
        title: t('name'),
        key: 'name',
        dataIndex: 'name',
      },
      {
        title: t('ministry.start-date'),
        key: 'startDate',
        dataIndex: 'startDate',
        width: TABLE_DATE_WIDTH,
        render: (date) => dateFormat({ datetime: date }) || FALLBACK_TEXT,
      },
      {
        title: t('ministry.end-date'),
        key: 'endDate',
        dataIndex: 'endDate',
        width: TABLE_DATE_WIDTH,
        render: (date) => dateFormat({ datetime: date }) || FALLBACK_TEXT,
      },
      {
        title: t('created-datetime'),
        key: 'createdAt',
        dataIndex: 'createdAt',
        width: TABLE_DATETIME_WIDTH,
        render: (value) => datetimeFormat({ datetime: value }),
      },
      {
        title: t('updated-datetime'),
        key: 'updatedAt',
        dataIndex: 'updatedAt',
        width: TABLE_DATETIME_WIDTH,
        render: (value) => datetimeFormat({ datetime: value }),
      },
      ...(editPermission.data && !isEditingOrder
        ? ([
            {
              title: '',
              key: 'actions',
              fixed: 'right',
              width: TABLE_ROW_MENU_WIDTH,
              onCell: () => ({
                style: { minWidth: TABLE_ROW_MENU_WIDTH },
              }),
              render: (_, record) => (
                <TableRowMenuCell
                  onEdit={() => {
                    setDrawerState({
                      open: true,
                      type: 'ministry',
                      isEditing: true,
                      id: record.id,
                    });
                  }}
                  items={[
                    {
                      key: 'delete',
                      label: t('delete'),
                      danger: true,
                      onClick: () => setDeletingId(record.id),
                    },
                  ]}
                />
              ),
            },
          ] satisfies TableColumnsType<z.infer<typeof MinistryModel>>)
        : []),
    ],
    [editPermission.data, setDrawerState, t, isEditingOrder],
  );

  if (ministries.isPending || !ministries.data) {
    return <Loading />;
  }

  return (
    <>
      {editPermission.data && !isEditingOrder && (
        <Flex justify="flex-end" style={{ marginBottom: 12 }}>
          <Button
            onClick={() => {
              setIsEditingOrder((prev) => !prev);
            }}
          >
            {t('sort-order-edit')}
          </Button>
        </Flex>
      )}

      {isEditingOrder ? (
        <MinistrySortableTable
          dataSource={ministries.data}
          columns={columns}
          onCancel={() => {
            setIsEditingOrder(false);
          }}
          onSubmitSuccess={() => {
            setIsEditingOrder(false);
          }}
        />
      ) : (
        <Table
          dataSource={ministries.data}
          rowKey="id"
          columns={columns}
          pagination={false}
        />
      )}

      <MinistryFormDrawer />
      <DeleteMinistryConfirmModal
        deletingId={deletingId}
        onClose={() => setDeletingId(null)}
      />
    </>
  );
};
